/**
 * Build a compact structure map describing repeatable / conditional sections
 * for the document-filler model.
 *
 * @param {unknown} formSchema
 * @returns {Record<string, object>}
 */
export function buildDocumentFillerFormStructure(formSchema) {
  const structure = {};
  if (!Array.isArray(formSchema)) return structure;

  for (const section of formSchema) {
    if (!section || typeof section !== "object" || !section.groupKey) continue;
    const groupKey = String(section.groupKey);

    if (section.repeatable) {
      structure[groupKey] = {
        kind: "repeatable",
        min_items: Number.isFinite(section.minItems) ? section.minItems : 0,
        item_fields: fieldIds(section.fields),
      };
      continue;
    }

    if (section.type === "conditional") {
      const options = {};
      if (section.options && typeof section.options === "object") {
        for (const [optionKey, fields] of Object.entries(section.options)) {
          options[optionKey] = fieldIds(fields);
        }
      }
      structure[groupKey] = {
        kind: "conditional",
        shape: { type: "<option_key>", fields: { FIELD_ID: "value" } },
        options,
      };
    }
  }

  return structure;
}

/**
 * Build a form_data scaffold the model can mirror: pad repeatables to minItems
 * with fully-keyed empty rows, and normalize unset conditionals to
 * `{ type: "", fields: {} }`.
 *
 * Existing user values are preserved and merged into the scaffold.
 *
 * @param {unknown} formSchema
 * @param {Record<string, unknown>} [formData]
 * @returns {Record<string, unknown>}
 */
export function buildDocumentFillerFormScaffold(formSchema, formData = {}) {
  const current =
    formData && typeof formData === "object" && !Array.isArray(formData)
      ? formData
      : {};
  const scaffold = { ...current };

  if (!Array.isArray(formSchema)) return scaffold;

  for (const section of formSchema) {
    if (!section || typeof section !== "object") continue;

    if (section.repeatable && section.groupKey) {
      const groupKey = String(section.groupKey);
      const minItems = Number.isFinite(section.minItems) ? section.minItems : 0;
      const targetLen = Math.max(minItems, 1);
      const existing = Array.isArray(current[groupKey]) ? current[groupKey] : [];
      const blank = emptyRow(section.fields);
      const rows = existing.map((row) =>
        row && typeof row === "object" && !Array.isArray(row)
          ? { ...blank, ...row }
          : { ...blank }
      );
      while (rows.length < targetLen) {
        rows.push({ ...blank });
      }
      scaffold[groupKey] = rows;
      continue;
    }

    if (section.type === "conditional" && section.groupKey) {
      const groupKey = String(section.groupKey);
      scaffold[groupKey] = scaffoldConditional(section, current[groupKey]);
      continue;
    }

    if (Array.isArray(section.fields)) {
      for (const field of section.fields) {
        if (!field?.id) continue;
        const id = String(field.id);
        if (scaffold[id] === undefined) {
          scaffold[id] = field.default != null ? field.default : "";
        }
      }
    }
  }

  return scaffold;
}

function fieldIds(fields) {
  if (!Array.isArray(fields)) return [];
  return fields
    .filter((field) => field && typeof field === "object" && field.id)
    .map((field) => String(field.id));
}

function emptyRow(fields) {
  const row = {};
  if (!Array.isArray(fields)) return row;
  for (const field of fields) {
    if (!field?.id) continue;
    row[String(field.id)] = field.default != null ? field.default : "";
  }
  return row;
}

function resolveOptionKey(section, value) {
  if (!value || !section.options) return value;
  const key = Object.keys(section.options).find(
    (k) => String(k).toLowerCase() === String(value).toLowerCase()
  );
  return key ?? value;
}

function scaffoldConditional(section, current) {
  const options = section.options || {};
  if (current && typeof current === "object" && !Array.isArray(current)) {
    const type = current.type ? resolveOptionKey(section, current.type) : "";
    const optionFields = type && options[type] ? options[type] : [];
    const fields = emptyRow(optionFields);
    const existingFields =
      current.fields && typeof current.fields === "object"
        ? current.fields
        : {};
    return {
      type: type || "",
      fields: { ...fields, ...existingFields },
    };
  }
  return { type: "", fields: {} };
}

export default buildDocumentFillerFormScaffold;
